import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useLogin } from './contexts/LoginContext';
import { useNavigate } from 'react-router-dom';
import { apiServer } from './api';
import { IToken } from "./types/token";
import { Alert, Collapse } from '@mui/material';
import { ChangeEvent, useState } from 'react';
import { sha256 } from './common/utils/funcoes';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

function Copyright(props: any) {
    return (
        <Typography variant="body2" color="text.secondary" align="center" {...props}>
            {'Direitos Reservados © Backup Fácil'}
            <Link color="inherit" href="https://mui.com/">

            </Link>{' '}
            {new Date().getFullYear()}
            {'.'}
        </Typography>
    );
}

const theme = createTheme({
    palette: {
        primary: {
            main: '#2563eb', // Azul premium moderno
        },
        secondary: {
            main: '#4f46e5',
        },
    },
});

export default function EfetuarLogin() {
    const [login, setLogin] = useLogin();
    const [senhaInvalida, setSenhaInvalida] = useState(false);
    const [email, setEmail] = useState("");
    const [senha, setSenha] = useState("");
    const [manterConectado, setManterConectado] = useState(false);
    const [mostrarSenha, setMostrarSenha] = useState(false);
    const navigate = useNavigate();

    const handleEmail = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setEmail(event.target.value);
        setSenhaInvalida(false);
    }

    const handleSenha = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setSenha(event.target.value);
        setSenhaInvalida(false);
    }

    const handleManterConectado = (event: React.ChangeEvent<HTMLInputElement>) => {
        setManterConectado(event.target.checked);
    };

    const handleClickMostrarSenha = () => {
        setMostrarSenha(!mostrarSenha);
    };

    const handleSubmit = async () => {
        apiServer<IToken>().post("/token", "", {
            usuario: email,
            senha: await sha256(senha),
            manterConectado,
            nomeAplicacao: "WebAdminBackupFacil"
        }).then((response) => {
            const token = response.data;
            setLogin({
                ...login,
                accessToken: token.accessToken,
                authenticated: true,
                usuario: token.usuario,
                state: "Logged"
            });

            navigate("/");
        }).catch((response) => {
            setSenhaInvalida(true);
        });
    };

    return (
        <ThemeProvider theme={theme}>
            <style>{`
                @keyframes moverNuvens {
                    0% { transform: translateY(0px) translateX(0px) scale(1); }
                    50% { transform: translateY(-25px) translateX(20px) scale(1.08); }
                    100% { transform: translateY(0px) translateX(0px) scale(1); }
                }
                @keyframes moverNuvensSecundaria {
                    0% { transform: translateY(0px) translateX(0px) scale(1.1); }
                    50% { transform: translateY(20px) translateX(-25px) scale(0.92); }
                    100% { transform: translateY(0px) translateX(0px) scale(1.1); }
                }
                @keyframes gradienteFundo {
                    0% { background-position: 0% 50%; }
                    50% { background-position: 100% 50%; }
                    100% { background-position: 0% 50%; }
                }
            `}</style>
            
            <Box
                sx={{
                    minHeight: '100vh',
                    width: '100vw',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    background: 'linear-gradient(-45deg, #f1f5f9, #e2e8f0, #f8fafc, #cbd5e1)',
                    backgroundSize: '400% 400%',
                    animation: 'gradienteFundo 20s ease infinite',
                }}
            >
                {/* Nuvem translúcida azul superior-esquerda */}
                <Box
                    sx={{
                        position: 'absolute',
                        width: { xs: '300px', sm: '600px' },
                        height: { xs: '300px', sm: '600px' },
                        borderRadius: '50%',
                        background: 'rgba(59, 130, 246, 0.12)',
                        filter: 'blur(100px)',
                        top: '-15%',
                        left: '-15%',
                        animation: 'moverNuvens 30s ease-in-out infinite',
                        pointerEvents: 'none',
                        zIndex: 0,
                    }}
                />

                {/* Nuvem translúcida violeta inferior-direita */}
                <Box
                    sx={{
                        position: 'absolute',
                        width: { xs: '350px', sm: '700px' },
                        height: { xs: '350px', sm: '700px' },
                        borderRadius: '50%',
                        background: 'rgba(99, 102, 241, 0.10)',
                        filter: 'blur(120px)',
                        bottom: '-20%',
                        right: '-15%',
                        animation: 'moverNuvensSecundaria 35s ease-in-out infinite',
                        pointerEvents: 'none',
                        zIndex: 0,
                    }}
                />

                <Container component="main" maxWidth="xs" sx={{ zIndex: 1 }}>
                    <CssBaseline />
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            padding: { xs: 3, sm: 5 },
                            borderRadius: '24px',
                            backgroundColor: 'rgba(255, 255, 255, 0.72)',
                            backdropFilter: 'blur(20px)',
                            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.06)',
                            border: '1px solid rgba(255, 255, 255, 0.75)',
                        }}
                    >
                        {/* Marca do Produto e Título */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                            <Box 
                                sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    width: 60, 
                                    height: 60, 
                                    borderRadius: '16px', 
                                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', 
                                    boxShadow: '0 8px 16px rgba(37, 99, 235, 0.2)',
                                    mb: 2
                                }}
                            >
                                <CloudQueueIcon sx={{ fontSize: 32, color: '#ffffff' }} />
                            </Box>
                            <Typography 
                                component="h1" 
                                variant="h4" 
                                sx={{ 
                                    fontWeight: 800, 
                                    letterSpacing: '-0.5px',
                                    color: '#0f172a',
                                    textAlign: 'center',
                                    fontFamily: '"Inter", "Roboto", sans-serif'
                                }}
                            >
                                Backup Fácil
                            </Typography>
                            <Typography 
                                variant="body2" 
                                sx={{ 
                                    color: '#64748b', 
                                    mt: 0.5, 
                                    textAlign: 'center',
                                    fontWeight: 500
                                }}
                            >
                                Painel de Gerenciamento na Nuvem
                            </Typography>
                        </Box>
                        <Box component="div" sx={{ mt: 1, width: '100%' }}>
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                id="email"
                                label="E-mail"
                                name="email"
                                autoComplete="email"
                                autoFocus
                                value={email}
                                onChange={handleEmail}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '12px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                    }
                                }}
                            />
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                name="password"
                                label="Senha"
                                type={mostrarSenha ? 'text' : 'password'}
                                id="password"
                                autoComplete="current-password"
                                value={senha}
                                onChange={handleSenha}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '12px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                    }
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="alternar visibilidade da senha"
                                                onClick={handleClickMostrarSenha}
                                                edge="end"
                                            >
                                                {mostrarSenha ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                            <Collapse in={login.mensagemSessaoExpirada || login.state === "SessionExpired"} sx={{ mt: 1 }}>
                                <Alert variant="outlined" severity="warning" sx={{ borderRadius: '12px' }}>
                                    Sua sessão expirou por inatividade. Faça login novamente para continuar.
                                </Alert>
                            </Collapse>
                            <Collapse in={senhaInvalida} sx={{ mt: 1 }}>
                                <Alert variant="outlined" severity="error" sx={{ borderRadius: '12px' }}>
                                    Usuário e/ou senha inválidos. Tente novamente.
                                </Alert>
                            </Collapse>
                            <FormControlLabel
                                control={
                                    <Checkbox 
                                        value="remember" 
                                        color="primary" 
                                        checked={manterConectado}
                                        onChange={handleManterConectado}
                                        sx={{ borderRadius: '4px' }}
                                    />
                                }
                                label="Manter conectado"
                                sx={{ mt: 1, '& .MuiFormControlLabel-label': { color: '#475569', fontSize: '0.9rem' } }}
                            />
                            <Button
                                type="button"
                                fullWidth
                                variant="contained"
                                sx={{ 
                                    mt: 3, 
                                    mb: 2, 
                                    borderRadius: '12px', 
                                    padding: '12px', 
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        boxShadow: '0 6px 20px rgba(37, 99, 235, 0.3)',
                                        transform: 'translateY(-1px)'
                                    }
                                }}
                                onClick={handleSubmit}
                            >
                                Entrar
                            </Button>

                            <Grid container sx={{ mt: 1 }}>
                                <Grid item xs>
                                    <Link href="/esqueceu-senha" variant="body2" sx={{ textDecoration: 'none', fontWeight: 500 }}>
                                        Esqueceu a senha?
                                    </Link>
                                </Grid>
                                <Grid item>
                                    <Link href="#" variant="body2" sx={{ textDecoration: 'none', fontWeight: 500 }}>
                                        {"Criar uma conta"}
                                    </Link>
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>
                    <Copyright sx={{ mt: 6, mb: 2, color: '#64748b' }} />
                </Container>
            </Box>
        </ThemeProvider>
    );
}